import React, { useState } from 'react'
import { Box, Divider } from '@mui/material'
import { ExpandMore, Group } from '@mui/icons-material'
import fullName from '../../../utils/fullName'
import getLocaleCurrencyForAmount from '../../../utils/getLocaleCurrencyForAmount'
import FamilyRow from './FamilyRow'
import { useTranslation } from 'react-i18next'
import type { FetchDirectorFinancialDetailsResponseTutors } from '../../../swagger'
import FinancialAmount from './common/FinancialAmount'
import FinancialLabel from './common/FinancialLabel'

interface TutorAccordionProps {
  tutor: FetchDirectorFinancialDetailsResponseTutors
  index: number
}

const TutorAccordion: React.FC<TutorAccordionProps> = ({ tutor, index }) => {
  const { t } = useTranslation()

  const [expanded, setExpanded] = useState(false)
  const isNotExpandable = tutor.enrollmentCount === 0

  const handleClick = () => {
    if (isNotExpandable) return
    setExpanded((prev) => !prev)
  }

  return (
    <Box>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: isNotExpandable ? 'cursor' : 'pointer',
          backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
          p: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Group
            sx={{
              mr: 2,
              color: (theme) => theme.palette.program.essentials,
              fontSize: 18,
            }}
          />
          <Box>
            <Box
              component="span"
              sx={{
                color: (theme) => theme.palette.primary.main,
                fontFamily: 'sweet-sans-pro, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {fullName(
                {
                  firstName: tutor.firstName,
                  lastName: tutor.lastName,
                },
                'Unassigned'
              )}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'block',
                color: (theme) => theme.palette.primary.dark,
                fontFamily: 'plantin, sans-serif',
                fontSize: '0.75rem',
              }}
            >
              {t(
                'TutorAccordion.FamiliesEnrolled.Label',
                '{{enrollmentCount}} families enrolled',
                {
                  enrollmentCount: tutor.enrollmentCount,
                }
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pr: 2,
            ml: 'auto',
          }}
        >
          <Box sx={{ textAlign: 'right' }}>
            <FinancialLabel sx={{ fontSize: '0.625rem' }}>
              {t(
                'TutorAccordion.TutorOwesDirector.Label',
                'Tutor Owes Director'
              )}
            </FinancialLabel>
            <FinancialAmount amountType="positive" sx={{ fontSize: '0.75rem' }}>
              {getLocaleCurrencyForAmount(
                tutor.tutorOwesDirector.amount,
                tutor.tutorOwesDirector.currencyCode
              )}
            </FinancialAmount>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ opacity: 0.3 }} />
          <Box sx={{ textAlign: 'right' }}>
            <FinancialLabel sx={{ fontSize: '0.625rem' }}>
              {t('TutorAccordion.FamiliesOweTutor.Label', 'Families Owe Tutor')}
            </FinancialLabel>
            <FinancialAmount amountType="positive" sx={{ fontSize: '0.75rem' }}>
              {getLocaleCurrencyForAmount(
                tutor.familiesOweTutor.amount,
                tutor.familiesOweTutor.currencyCode
              )}
            </FinancialAmount>
          </Box>
        </Box>

        {!isNotExpandable && (
          <ExpandMore
            sx={{
              color: (theme) => theme.palette.primary.main,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              fontSize: 16,
            }}
          />
        )}
      </Box>

      {expanded && (
        <Box sx={{ backgroundColor: '#f8f9ff', p: 0 }}>
          {tutor.families.map(
            ({ firstName, lastName, familyOwesTutor }, index) => (
              <FamilyRow
                key={`${firstName}-${lastName}`}
                index={index}
                family={{
                  name: fullName({
                    firstName,
                    lastName,
                  }),
                  amount: familyOwesTutor,
                }}
              />
            )
          )}
        </Box>
      )}
    </Box>
  )
}

export default TutorAccordion
